<template>
  <v-card class="pa-6">
    <overlay :value="loading"></overlay>
    <v-card-title class="text-uppercase">
      <v-row class="my-1 justify-space-between align-center">
        <div>Defaults por país — {{ serviceName }}</div>
      </v-row>
    </v-card-title>
    <hr class="my-3" />
    <v-card-text class="my-0 py-0">
      <template v-if="tabs.length">
        <v-tabs v-model="activeTab" show-arrows>
          <v-tab v-for="tab in tabs" :key="tab.countryId">{{ tab.countryName }}</v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeTab">
          <v-tab-item v-for="tab in tabs" :key="tab.countryId">
            <v-container class="ma-0 pa-0 pt-4">
              <div class="text-caption grey--text mb-2">
                "De qué trata el servicio" es igual en todos los países y se edita en el servicio.
              </div>
              <v-row class="my-1">
                <v-col cols="4">
                  <v-text-field
                    label="Límite anual"
                    v-model.number="tab.maxAnnualAmount"
                    outlined
                    dense
                    type="number"
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="4">
                  <v-text-field
                    label="Límite por evento"
                    v-model.number="tab.maxAmountPerEvent"
                    outlined
                    dense
                    type="number"
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="4">
                  <v-text-field
                    label="Máx. eventos al año"
                    v-model.number="tab.maxAnnualEvents"
                    outlined
                    dense
                    type="number"
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="my-1">
                <v-col cols="6">
                  <v-text-field
                    label="Días de carencia proveedor"
                    v-model.number="tab.graceDays"
                    outlined
                    dense
                    type="number"
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    label="Días de revisión"
                    v-model.number="tab.refundReviewDays"
                    outlined
                    dense
                    type="number"
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="my-1 justify-end">
                <v-col cols="auto">
                  <v-btn color="primary" rounded :loading="tab.saving" @click="saveTab(tab)">
                    Guardar {{ tab.countryName }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>
        </v-tabs-items>
      </template>
      <div v-else-if="!loading" class="text-center grey--text pa-6">
        No hay países configurados en el sistema.
      </div>
    </v-card-text>
    <hr class="my-3" />
    <v-card-actions class="justify-end">
      <v-btn text color="primary" rounded @click="$emit('close-dialog')">Cerrar</v-btn>
    </v-card-actions>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-card>
</template>

<script>
import Overlay from '../components/Overlay.vue';
import ServicesService from '../services/services.service';
import CountryService from '../services/countries.service';
import { utilsMixin } from '../mixins/utilsMixin';

export default {
  name: 'ServiceDefaultsDialog',
  components: { Overlay },
  mixins: [utilsMixin],
  props: {
    serviceId: { type: String, default: null },
    serviceName: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      tabs: [],
      activeTab: 0,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'red'
    };
  },
  watch: {
    serviceId: {
      immediate: true,
      handler(val) {
        if (val) this.loadData();
      }
    }
  },
  methods: {
    async loadData() {
      this.loading = true;
      const [countriesRes, defaultsRes] = await Promise.all([
        CountryService.getAllCountries(true),
        ServicesService.getServicePlanDefaults(this.serviceId)
      ]);
      const countries = countriesRes && countriesRes.status === 'ok' ? countriesRes.data : [];
      const defaults = defaultsRes && defaultsRes.status === 'ok' ? defaultsRes.data : [];
      if (!countriesRes || countriesRes.status !== 'ok') this.showErrorSnackbar('Error al cargar los países');
      if (!defaultsRes || defaultsRes.status !== 'ok') {
        this.showErrorSnackbar('Error al cargar los defaults del servicio');
      }
      this.tabs = countries.map(country =>
        this.buildTab(country, defaults.find(d => d.countryId === country.id))
      );
      this.activeTab = 0;
      this.loading = false;
    },
    buildTab(country, existing) {
      return {
        countryId: country.id,
        countryName: country.name,
        id: existing ? existing.id : null,
        maxAnnualAmount: existing && existing.maxAnnualAmount != null ? existing.maxAnnualAmount : 0,
        maxAnnualEvents: existing && existing.maxAnnualEvents != null ? existing.maxAnnualEvents : 0,
        graceDays: existing && existing.graceDays != null ? existing.graceDays : 0,
        refundReviewDays: existing && existing.refundReviewDays != null ? existing.refundReviewDays : 0,
        maxAmountPerEvent: existing && existing.maxAmountPerEvent != null ? existing.maxAmountPerEvent : 0,
        saving: false
      };
    },
    buildPayload(tab) {
      return {
        serviceId: this.serviceId,
        countryId: tab.countryId,
        maxAnnualAmount: tab.maxAnnualAmount,
        maxAnnualEvents: tab.maxAnnualEvents,
        graceDays: tab.graceDays,
        refundReviewDays: tab.refundReviewDays,
        maxAmountPerEvent: tab.maxAmountPerEvent
      };
    },
    async saveTab(tab) {
      tab.saving = true;
      let res;
      if (tab.id) {
        const payload = this.buildPayload(tab);
        delete payload.serviceId;
        delete payload.countryId;
        res = await ServicesService.updateServicePlanDefault(tab.id, payload);
      } else {
        res = await ServicesService.createServicePlanDefault(this.buildPayload(tab));
      }
      tab.saving = false;
      if (res.status === 'ok') {
        if (res.data && res.data.id) tab.id = res.data.id;
        this.showSuccessSnackbar(`Default de ${tab.countryName} guardado correctamente`);
      } else {
        this.showErrorSnackbar(res.message || 'Ocurrió un error guardando el default, intenta más tarde');
      }
    }
  }
};
</script>
